import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchMyPatient } from "../../store/slices/patientSlice";
import { fetchTestOrders } from "../../store/slices/testOrderSlice";
import { fetchTests } from "../../store/slices/testSlice";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { currentPatient, loading: patientLoading, error: patientError } =
    useSelector((s) => s.patients || {});
  const {
    testOrders,
    loading: ordersLoading,
    error: ordersError,
  } = useSelector((s) => s.testOrders || {});
  const { tests } = useSelector((s) => s.tests || { tests: [] });

  const ordersList = useMemo(() => {
    if (Array.isArray(testOrders)) return testOrders;
    return testOrders?.data || [];
  }, [testOrders]);

  const testsById = useMemo(() => {
    const list = Array.isArray(tests) ? tests : tests?.data || [];
    return Object.fromEntries(list.map((t) => [t.id, t]));
  }, [tests]);

  const refreshAll = async () => {
    const profile = await dispatch(fetchMyPatient()).unwrap().catch(() => null);
    const patientId = profile?.id || currentPatient?.id;
    if (patientId) {
      dispatch(fetchTestOrders({ patient_id: patientId }));
    }
    if (!Array.isArray(tests) || tests.length === 0) dispatch(fetchTests());
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (!currentPatient?.id) return;
    dispatch(fetchTestOrders({ patient_id: currentPatient.id }));
  }, [dispatch, currentPatient?.id]);

  // Minimal "realtime": poll + refresh when tab becomes visible
  useEffect(() => {
    const tick = () => {
      dispatch(fetchMyPatient());
      if (currentPatient?.id) {
        dispatch(fetchTestOrders({ patient_id: currentPatient.id }));
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", onVisibility);

    const id = window.setInterval(tick, 15000);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", tick);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch, currentPatient?.id]);

  const myOrders = useMemo(() => {
    return ordersList
      .map((o) => ({
        ...o,
        test_name: testsById[o.test_id]?.name || `Test #${o.test_id}`,
      }))
      .sort(
        (a, b) => new Date(b.ordered_at || 0) - new Date(a.ordered_at || 0)
      );
  }, [ordersList, testsById]);

  const recent = useMemo(() => myOrders.slice(0, 5), [myOrders]);

  const isLoading = patientLoading || ordersLoading;
  const error = patientError || ordersError;

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString();
  };

  const statusBadge = (status) => {
    const map = {
      ordered: "bg-blue-100 text-blue-800 border-blue-200",
      sample_collected: "bg-purple-100 text-purple-800 border-purple-200",
      in_progress: "bg-orange-100 text-orange-800 border-orange-200",
      completed: "bg-green-100 text-green-800 border-green-200",
      ready_for_pathologist: "bg-amber-100 text-amber-800 border-amber-200",
      cancelled: "bg-gray-100 text-gray-700 border-gray-200",
    };
    const cls = map[status] || map.cancelled;
    return `inline-flex items-center px-2 py-1 text-xs rounded-full border ${cls}`;
  };

  return (
    <div className="mx-auto w-full max-w-7xl p-6">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-500">Patient Portal</div>
          <h1 className="mt-1 text-2xl font-bold text-[#235F72]">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Your profile and most recent laboratory orders.
          </p>
        </div>
        <div className="shrink-0">
          <button
            onClick={() => refreshAll()}
            className="px-4 py-2 bg-[#235F72] text-white rounded-lg hover:bg-[#1a4a5a]"
            disabled={isLoading}
          >
            {isLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {String(error)}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Personal Information */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#235F72]">Personal Information</h2>
              <button
                className="text-sm font-semibold text-[#085DB6] hover:text-[#074a9b]"
                onClick={() => navigate("/patient/profile")}
                disabled={!currentPatient}
              >
                View
              </button>
            </div>

            {isLoading && !currentPatient ? (
              <div className="mt-4 text-sm text-gray-600">Loading profile…</div>
            ) : currentPatient ? (
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between border-b pb-2">
                  <span className="font-medium text-gray-600">Name:</span>
                  <span className="text-[#235F72]">{currentPatient.full_name || "-"}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="font-medium text-gray-600">Card No:</span>
                  <span className="text-[#235F72]">{currentPatient.card_number || "-"}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="font-medium text-gray-600">Phone:</span>
                  <span className="text-[#235F72]">{currentPatient.phone || "-"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Gender:</span>
                  <span className="text-[#235F72]">{currentPatient.gender || "-"}</span>
                </div>
              </div>
            ) : (
              <div className="mt-4 text-sm text-gray-600">No profile data available.</div>
            )}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#235F72]">Recent Orders</h2>
              <button
                className="text-sm font-semibold text-[#085DB6] hover:text-[#074a9b]"
                onClick={() => navigate("/patient/history")}
              >
                See all
              </button>
            </div>

            {isLoading && recent.length === 0 ? (
              <div className="mt-4 text-sm text-gray-600">Loading orders…</div>
            ) : recent.length ? (
              <div className="mt-4 space-y-3">
                {recent.map((o) => (
                  <div
                    key={o.id}
                    className="rounded-lg border border-gray-200 p-4 hover:border-[#36F1A2] transition"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-[#235F72]">#{o.id}</div>
                          <span className={statusBadge(o.status)}>
                            {(o.status || "-").replaceAll("_", " ")}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-700 truncate">
                          {o.test_name}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Ordered: {formatDateTime(o.ordered_at)}
                        </div>
                      </div>

                      <div className="shrink-0 flex items-center gap-2">
                        <button
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                            o.status === "completed"
                              ? "bg-[#085DB6] text-white hover:bg-[#074a9b]"
                              : "bg-gray-100 text-gray-400 cursor-not-allowed"
                          }`}
                          disabled={o.status !== "completed"}
                          onClick={() =>
                            navigate(`/print-report?orderId=${encodeURIComponent(o.id)}`)
                          }
                        >
                          View results
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-6 text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">📄</div>
                <p>No orders found yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
